import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { authorizedFetch, noAuthorizedFetch, getAccessToken } from '../utils/api';
import FileViewer from './FileViewer';
import FileTypeIcon from './FileTypeIcon';

function FileCard({ file, sessionId }) {
    const [downloading, setDownloading] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);

    const handleDownload = async (e) => {
        e.stopPropagation();
        if (downloading) return;
        setDownloading(true);
        try {
            const safeFetch = getAccessToken() ? authorizedFetch : noAuthorizedFetch;
            const data = await safeFetch(
                `/v2/sessions/${sessionId}/files/content?file_path=${encodeURIComponent(file.path)}`
            );
            if (data.download_url) {
                const res = await fetch(data.download_url);
                const blob = await res.blob();
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = file.filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(blobUrl);
            }
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            <div
                onClick={() => setViewerOpen(true)}
                className="flex items-center gap-1.5 p-2 pr-2.5 w-full min-w-[240px] group/attach relative overflow-hidden cursor-pointer rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all"
            >
                <div className="flex justify-center items-center w-8 h-8 rounded-md flex-shrink-0">
                    <FileTypeIcon iconType={file.icon_type} size={32} />
                </div>
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <div className="text-sm text-gray-800 truncate" title={file.filename}>{file.filename}</div>
                </div>
                <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hidden group-hover/attach:block"
                    title="Download"
                >
                    {downloading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Download className="w-4 h-4" />
                    )}
                </button>
            </div>
            {viewerOpen && (
                <FileViewer
                    file={file}
                    sessionId={sessionId}
                    onClose={() => setViewerOpen(false)}
                />
            )}
        </>
    );
}

export default function FilesBlock({ title, files, sessionId }) {
    if (!files || files.length === 0) return null;

    return (
        <div className="flex flex-col gap-2 max-w-[min(100%,570px)] pt-1">
            {title && (
                <div className="text-[13px] font-medium text-gray-600">{title}</div>
            )}
            <div className="w-full grid md:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] grid-cols-1 gap-2 max-w-[568px]">
                {files.map((file, i) => (
                    <FileCard key={file.id || file.path || i} file={file} sessionId={sessionId} />
                ))}
            </div>
        </div>
    );
}
